/**
 * Der Bürochat am Einsatz — `PWA-005`, nach `07` §7.10.
 *
 * **Er löst ein benanntes Problem**, und zwar nicht „Kommunikation", sondern
 * dieses: „Kundendaten in privaten Messengern auf Monteurhandys." Ein Raum je
 * Auftrag heißt, dass die Unterhaltung dort bleibt, wo sie hingehört — nicht in
 * einem Gruppenchat, der beim Ausscheiden eines Mitarbeiters mitgeht.
 *
 * **Er funktioniert im Funkloch.** Ein Beitrag geht durch dieselbe Outbox wie
 * Zeiten und Fotos: `07` §7.10 nennt den Chat als Weg für Rückfragen vor Ort,
 * und vor Ort ist regelmäßig kein Netz. Ein Chat, der nur online funktioniert,
 * wäre für seinen eigenen Zweck unbrauchbar.
 *
 * **Anrufe gibt es nicht** (ADR-018) — und das ist keine Lücke, sondern eine
 * Entscheidung: „Der auftragsbezogene Chat löst das eigentliche Problem. Für
 * Sprache bleibt der normale Telefonanruf über den `tel:`-Link."
 */

import { useState } from "react";

import { findePerson } from "@hwc/demo-data";
import type { Einsatz } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { useMonteur } from "../../src/Sitzung.js";
import { Grossknopf, Knopfleiste, Leer, uhrzeit } from "../../src/bausteine.js";

export function Buerochat({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, monteur, online, schreibeChat, eigeneBeitraege } = useMonteur();
  const [text, setzeText] = useState("");

  const raum = bestand.chatraeume.find(
    (r) => formatResourceRef(r.auftrag) === formatResourceRef(einsatz.auftrag),
  );

  if (!raum) {
    return (
      <>
        <h2>Bürochat</h2>
        {/*
          `07` §7.10: „Jeder Auftrag **kann** einen Nextcloud-Talk-Raum
          besitzen." Nicht jeder hat einen, und das ist der häufigere Fall.
        */}
        <Leer text="Für diesen Auftrag gibt es keinen Raum. Bei einer Rückfrage hilft der Anruf." />
      </>
    );
  }

  const gesendet = bestand.chatbeitraege.filter(
    (b) => formatResourceRef(b.raum) === formatResourceRef(raum.ref),
  );
  const eigene = eigeneBeitraege(raum.ref);

  return (
    <>
      <h2>Bürochat</h2>

      {gesendet.length === 0 && eigene.length === 0 ? (
        <Leer text="Noch nichts geschrieben." />
      ) : (
        <div className="chatverlauf">
          {gesendet.map((b) => (
            <p key={b.ref.opaqueId} className="chatbeitrag">
              <strong>{findePerson(bestand, b.autor)?.kuerzel ?? "??"}</strong> {b.text}
              <span className="leise"> · {uhrzeit(b.gesendet)}</span>
            </p>
          ))}
          {eigene.map((b) => (
            <p key={b.id} className="chatbeitrag chatbeitrag--wartend">
              <strong>{monteur.kuerzel}</strong> {b.text}
              <span className="abzeichen abzeichen--warnung">wartet auf Upload</span>
            </p>
          ))}
        </div>
      )}

      <textarea
        className="notizfeld"
        rows={2}
        value={text}
        placeholder="Rückfrage ans Büro"
        onChange={(e) => setzeText(e.target.value)}
      />

      <Knopfleiste>
        <Grossknopf
          art="haupt"
          disabled={!text.trim()}
          onClick={() => {
            schreibeChat(einsatz, raum.ref, text);
            setzeText("");
          }}
        >
          Senden
        </Grossknopf>
      </Knopfleiste>

      <p className="leise fussnote">
        {online
          ? "Geht mit der nächsten Übertragung raus."
          : "Im Funkloch: Der Beitrag wartet, bis wieder Netz da ist — schreiben können Sie trotzdem."}{" "}
        Anrufe gibt es hier nicht (ADR-018); dafür ist das Telefon da.
      </p>
    </>
  );
}
